import { inject, Injectable } from '@angular/core';
import { EAnalyticsStatus, EMatchingType } from '@enums/campaign-analytics.enum';
import {
  ICampaignAnalyticsApi,
  ICreatorCtaKpiStatusApi,
  ICtaKpiStatusApi,
  IPlatformInformationApi,
} from '@interfaces/campaign-analytics-api.interface';
import { IAnalyticsNode, ICreatorAnalytics, ICtaKpiStatus } from '@interfaces/campaign-analytics.interface';
import { ICampaignCreator, TCampaignApi } from '@pages/campaigns/campaigns.interface';
import { IChannel } from '@pages/creators/creators.interface';
import { CampaignAnalyticsUtilitiesService } from '@utilities/campaign-analytics-utilities.service';
import { DateUtilitiesService } from '@utilities/date-utilities.service';
import { camelCase } from 'lodash';
import { ICampaignDetailsCreator } from '../campaign-details.interface';
import { CampaignDetailsService } from '../campaign-details.service';

@Injectable({
  providedIn: 'root',
})
export class CampaignDetailsCreatorsService {
  private readonly campaignDetailsService = inject(CampaignDetailsService);
  private readonly campaignAnalyticsUtilities = inject(CampaignAnalyticsUtilitiesService);
  private readonly dateUtils = inject(DateUtilitiesService);

  public buildCampaignDetailsCreators(
    campaign: TCampaignApi,
    creatorsCtaKpiStatus: ICreatorCtaKpiStatusApi[],
    campaignAnalyticsApi: ICampaignAnalyticsApi | null | undefined,
    analyticsNodes?: IAnalyticsNode[] | null,
  ): ICampaignDetailsCreator[] {
    const analyticsCreators = this.getCreatorsFromAnalytics(campaign, campaignAnalyticsApi);
    const campaignCreators = new Map((campaign.campaignCreators ?? []).map((cc) => [cc.creatorId, cc]));
    const creatorsCtaKpiStatusMap = new Map(creatorsCtaKpiStatus.map((c) => [c.creatorId, c.ctaKpiStatus]));
    const creatorIds = Array.from(
      new Set([...analyticsCreators.keys(), ...campaignCreators.keys(), ...creatorsCtaKpiStatusMap.keys()]),
    );
    const nodeStatsByCreator =
      !campaignAnalyticsApi && analyticsNodes
        ? analyticsNodes.reduce<Map<string, { total: number; missing: number }>>((map, node) => {
            const stats = map.get(node.creatorId) ?? { missing: 0, total: 0 };

            stats.total++;

            if (node.executionStatus === EMatchingType.missing) {
              stats.missing++;
            }

            map.set(node.creatorId, stats);

            return map;
          }, new Map())
        : null;

    return creatorIds
      .map((creatorId) => {
        const analyticsCreator = analyticsCreators.get(creatorId);
        const campaignCreator = campaignCreators.get(creatorId);
        const creatorData = analyticsCreator ?? this.getFallbackCreatorData(campaignCreator);
        const ctaKpiStatus =
          creatorsCtaKpiStatusMap.get(creatorId) ?? this.campaignDetailsService.createEmptyCtaKpiStatusApi();

        if (!creatorData) {
          return null;
        }

        const built: ICampaignDetailsCreator = {
          ...creatorData,
          ...this.getCtaKpiStatusDisplayData(ctaKpiStatus),
        };

        if (nodeStatsByCreator) {
          const stats = nodeStatsByCreator.get(creatorId) ?? { missing: 0, total: 0 };

          built.ctaKpiStatus = {
            ...built.ctaKpiStatus,
            currentMissingContents: stats.missing,
            expectedContents: stats.total,
          };
        }

        return built;
      })
      .filter((creator): creator is ICampaignDetailsCreator => creator !== null);
  }

  private getCreatorsFromAnalytics(
    campaign: TCampaignApi,
    campaignAnalyticsApi: ICampaignAnalyticsApi | null | undefined,
  ): Map<string, ICreatorAnalytics> {
    if (!campaignAnalyticsApi) {
      return new Map();
    }

    const [campaignAnalytics] = this.campaignDetailsService.getCampaignDetailsTableData(campaign, campaignAnalyticsApi);

    return campaignAnalytics.creators;
  }

  private getFallbackCreatorData(
    campaignCreator?: ICampaignCreator,
  ): Omit<ICampaignDetailsCreator, 'color' | 'ctaKpiStatus' | 'status' | 'statusKpi' | 'mergedStatus'> | null {
    if (!campaignCreator?.creator) {
      return null;
    }

    return {
      icon: campaignCreator.creator.icon ?? null,
      id: campaignCreator.creatorId,
      name: campaignCreator.creator.name,
      usedPlatforms: this.mapChannelsToUsedPlatforms(campaignCreator.creator.channels),
    };
  }

  private mapChannelsToUsedPlatforms(channels?: IChannel[]): IPlatformInformationApi[] {
    return (
      channels?.map((channel) => ({
        platform: camelCase(channel.platform) as IPlatformInformationApi['platform'],
        platformId: channel.id,
      })) ?? []
    );
  }

  private getCtaKpiStatusDisplayData(
    ctaKpiStatusApi: ICtaKpiStatusApi,
  ): Pick<ICampaignDetailsCreator, 'color' | 'ctaKpiStatus' | 'status' | 'statusKpi' | 'mergedStatus'> {
    const { lastProcessed, ...ctaKpiStatusData } = ctaKpiStatusApi;
    const status = this.getStatusFromCtaKpiStatus(ctaKpiStatusApi, false);
    const statusKpi = this.getStatusFromCtaKpiStatus(ctaKpiStatusApi, true);
    const mergedStatus = this.getMergedAnalyticsStatus(new Set([status, statusKpi]));
    const ctaKpiStatus: ICtaKpiStatus = {
      ...ctaKpiStatusData,
      deliverableColor: this.campaignAnalyticsUtilities.getStatusableDisplayConfig(status).color,
      kpiColor: this.campaignAnalyticsUtilities.getStatusableDisplayConfig(statusKpi).color,
      lastProcessed: lastProcessed ? this.dateUtils.parseDateTimeFromApi(lastProcessed) : null,
    };
    const { color, status: mergedStatusDisplay } =
      this.campaignAnalyticsUtilities.getStatusableDisplayConfig(mergedStatus);

    return {
      color,
      ctaKpiStatus,
      mergedStatus: mergedStatusDisplay,
      status,
      statusKpi,
    };
  }

  private getStatusFromCtaKpiStatus(ctaKpiStatus: ICtaKpiStatusApi, isKpi = false): EAnalyticsStatus {
    const failedCount = isKpi
      ? ctaKpiStatus.currentFailedKpis
      : ctaKpiStatus.currentFailedDeliverables +
        ctaKpiStatus.currentFailedContents +
        ctaKpiStatus.currentMissingContents;
    const problemCount = isKpi
      ? ctaKpiStatus.currentProblemKpis
      : ctaKpiStatus.currentProblemDeliverables + ctaKpiStatus.currentProblemContents;
    const processingCount = isKpi
      ? ctaKpiStatus.currentProcessingKpis
      : ctaKpiStatus.currentProcessingDeliverables +
        Number(
          ctaKpiStatus.currentMissingContents === 0 &&
            ctaKpiStatus.currentMatchedContents === 0 &&
            ctaKpiStatus.currentBonusContents === 0,
        );
    const okCount = isKpi
      ? ctaKpiStatus.currentOkKpis
      : ctaKpiStatus.currentOkDeliverables + ctaKpiStatus.currentOkContents;
    const statuses = new Set<EAnalyticsStatus>();

    if (failedCount > 0) {
      statuses.add(EAnalyticsStatus.failed);
    }

    if (problemCount > 0) {
      statuses.add(EAnalyticsStatus.problem);
    }

    if (processingCount > 0) {
      statuses.add(EAnalyticsStatus.processing);
    }

    if (okCount > 0) {
      statuses.add(EAnalyticsStatus.ok);
    }

    if (statuses.size === 1 && statuses.has(EAnalyticsStatus.failed)) {
      return EAnalyticsStatus.failed;
    }

    if ((statuses.size === 1 && statuses.has(EAnalyticsStatus.processing)) || statuses.size === 0) {
      return EAnalyticsStatus.processing;
    }

    if (!statuses.has(EAnalyticsStatus.problem) && !statuses.has(EAnalyticsStatus.failed)) {
      return EAnalyticsStatus.ok;
    }

    return EAnalyticsStatus.problem;
  }

  private getMergedAnalyticsStatus(statuses: Set<EAnalyticsStatus>): EAnalyticsStatus {
    if (statuses.size === 1 && statuses.has(EAnalyticsStatus.failed)) {
      return EAnalyticsStatus.failed;
    }

    if ((statuses.size === 1 && statuses.has(EAnalyticsStatus.processing)) || statuses.size === 0) {
      return EAnalyticsStatus.processing;
    }

    if (!statuses.has(EAnalyticsStatus.problem) && !statuses.has(EAnalyticsStatus.failed)) {
      return EAnalyticsStatus.ok;
    }

    return EAnalyticsStatus.problem;
  }
}
